import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { createBusiness } from "../../../store/business/businessReducer";
import { useNavigate } from "react-router-dom";
import { PaperClipIcon } from "@heroicons/react/20/solid";

const ReviewAndSubmit = ({
    onEdit,
}: {
    onEdit: (section: string) => void;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const formData = useSelector((state: RootState) => state.business);

    const handleSubmit = async () => {
        try {
            await dispatch(createBusiness());
            navigate("/user/businesses");
        } catch (error) {
            console.error("Failed to submit business:", error);
        }
    };

    const renderField = (label: string, value: any) => (
        <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">{label}</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-2">
                {Array.isArray(value) ? (
                    <ul className="list-disc list-inside">
                        {value.map((item, index) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                ) : typeof value === "object" ? (
                    <pre className="text-sm text-gray-800">
                        {JSON.stringify(value, null, 2)}
                    </pre>
                ) : (
                    value || "N/A"
                )}
            </dd>
        </div>
    );

    return (
        <div>
            <div className="px-4 sm:px-0">
                <h3 className="text-lg font-semibold text-gray-900">Review & Submit</h3>
                <p className="mt-1 text-sm text-gray-500">
                    Review the information you provided below. If everything looks good,
                    click "Submit" to complete the process.
                </p>
            </div>
            <div className="mt-6">
                <dl className="grid grid-cols-1 sm:grid-cols-2">
                    {/* Business Information */}
                    {renderField(
                        "Business Name",
                        formData.businessInfo.businessName
                    )}
                    {renderField("Owner Name", formData.businessInfo.ownerName)}
                    {renderField("Email", formData.businessInfo.businessEmail)}
                    {renderField("Phone", formData.businessInfo.phone)}
                    {renderField("Address", formData.businessInfo.address)}

                    {/* Categories Information */}
                    {renderField(
                        "Business Category",
                        formData.categories.businessCategory
                    )}
                    {renderField("Subcategory", formData.categories.subcategory)}
                    {renderField("Website", formData.categories.websiteURL)}
                    {renderField(
                        "Social Media Links",
                        formData.categories.socialMediaLinks
                    )}

                    {/* Media Information */}
                    <div className="border-t border-gray-100 px-4 py-6 sm:col-span-2 sm:px-0">
                        <dt className="text-sm font-medium text-gray-900">
                            Uploaded Images
                        </dt>
                        <dd className="mt-2 text-sm text-gray-900">
                            <ul
                                role="list"
                                className="divide-y divide-gray-100 rounded-md border border-gray-200"
                            >
                                {formData.mediaInfo.uploadedImages?.map(
                                    (url: string, index: number) => (
                                        <li
                                            key={index}
                                            className="flex items-center justify-between py-4 pl-4 pr-5 text-sm"
                                        >
                                            <div className="flex w-0 flex-1 items-center">
                                                <PaperClipIcon
                                                    aria-hidden="true"
                                                    className="size-5 shrink-0 text-gray-400"
                                                />
                                                <div className="ml-4 flex min-w-0 flex-1 gap-2">
                                                    <span className="truncate font-medium">
                                                        {url.split("/").pop()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="ml-4 shrink-0">
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-medium text-indigo-600 hover:text-indigo-500"
                                                >
                                                    View
                                                </a>
                                            </div>
                                        </li>
                                    )
                                )}
                            </ul>
                        </dd>
                    </div>

                    {/* Working Hours */}
                    <div className="border-t border-gray-100 px-4 py-6 sm:col-span-2 sm:px-0">
                        <dt className="text-sm font-medium text-gray-900">
                            Working Hours
                        </dt>
                        <dd className="mt-2 text-sm text-gray-700">
                            {Object.entries(formData.workingHours).map(
                                ([day, slots]: [string, any]) => (
                                    <div key={day}>
                                        <span className="font-medium capitalize">
                                            {day}:
                                        </span>
                                        <ul className="ml-4 list-disc list-inside">
                                            {slots.map(
                                                (
                                                    slot: {
                                                        openHours: string;
                                                        openMinutes: string;
                                                        closeHours: string;
                                                        closeMinutes: string;
                                                    },
                                                    index: number
                                                ) => (
                                                    <li key={index}>
                                                        {`${slot.openHours}:${slot.openMinutes} - ${slot.closeHours}:${slot.closeMinutes}`}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )
                            )}
                        </dd>
                    </div>
                </dl>
            </div>
            <div className="mt-6 flex justify-end">
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default ReviewAndSubmit;
